export interface ModelPricing {
    inputPerMillion: number;
    outputPerMillion: number;
    longContextThreshold?: number;
    longContextInputPerMillion?: number;
    longContextOutputPerMillion?: number;
}

export interface MessageLlmUsage {
    model?: string | null;
    input_tokens?: number | null;
    output_tokens?: number | null;
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    total_tokens?: number | null;
    provider?: string | null;
}

// USD per 1M tokens. Keep in sync with backend/api/agents/llm/models.py.
const MODEL_PRICING_USD_PER_MILLION: Record<string, ModelPricing> = {
    // --- OpenAI ---
    // GPT-6 family (2026-09-03): Astra flagship, Sol mid-tier, Luna small.
    // Above 272K input tokens the whole request bills at 2x input / 1.5x output.
    "gpt-6.1-sol": {
        inputPerMillion: 2.0,
        outputPerMillion: 10.0,
        longContextThreshold: 272_001,
        longContextInputPerMillion: 4.0,
        longContextOutputPerMillion: 15.0,
    },
    "gpt-6-astra": {
        inputPerMillion: 10.0,
        outputPerMillion: 50.0,
        longContextThreshold: 272_001,
        longContextInputPerMillion: 20.0,
        longContextOutputPerMillion: 75.0,
    },
    "gpt-6-sol": {
        inputPerMillion: 2.0,
        outputPerMillion: 10.0,
        longContextThreshold: 272_001,
        longContextInputPerMillion: 4.0,
        longContextOutputPerMillion: 15.0,
    },
    "gpt-6-luna": {
        inputPerMillion: 0.1,
        outputPerMillion: 0.5,
        longContextThreshold: 272_001,
        longContextInputPerMillion: 0.2,
        longContextOutputPerMillion: 0.75,
    },
    // Sol repriced with the GPT-6 launch (was 5.0 / 30.0).
    "gpt-5.6-sol": { inputPerMillion: 4.0, outputPerMillion: 20.0 },
    // Terra/Luna repriced 2026-07-30 (Luna -80%, Terra -20%).
    "gpt-5.6-terra": { inputPerMillion: 2.0, outputPerMillion: 12.0 },
    "gpt-5.6-luna": { inputPerMillion: 0.2, outputPerMillion: 1.2 },
    "gpt-5.5": { inputPerMillion: 5.0, outputPerMillion: 30.0 },
    "gpt-5.4": { inputPerMillion: 2.5, outputPerMillion: 15.0 },
    "gpt-5.4-mini": { inputPerMillion: 0.75, outputPerMillion: 4.5 },
    "gpt-5.3-codex": { inputPerMillion: 1.75, outputPerMillion: 14.0 },
    "gpt-5.2": { inputPerMillion: 1.75, outputPerMillion: 14.0 },
    "gpt-5-mini": { inputPerMillion: 0.25, outputPerMillion: 2.0 },
    "gpt-5-nano": { inputPerMillion: 0.05, outputPerMillion: 0.4 },
    "gpt-5": { inputPerMillion: 1.25, outputPerMillion: 10.0 },
    "gpt-4.1": { inputPerMillion: 2.0, outputPerMillion: 8.0 },
    "gpt-4.1-mini": { inputPerMillion: 0.4, outputPerMillion: 1.6 },
    "gpt-4.1-nano": { inputPerMillion: 0.1, outputPerMillion: 0.4 },
    "gpt-4o": { inputPerMillion: 2.5, outputPerMillion: 10.0 },
    "gpt-4o-mini": { inputPerMillion: 0.15, outputPerMillion: 0.6 },
    // --- Anthropic ---
    "claude-fable-5-1": { inputPerMillion: 10.0, outputPerMillion: 50.0 },
    "claude-fable-5": { inputPerMillion: 10.0, outputPerMillion: 50.0 },
    "claude-opus-5-5": { inputPerMillion: 4.0, outputPerMillion: 20.0 },
    "claude-opus-5": { inputPerMillion: 5.0, outputPerMillion: 25.0 },
    "claude-opus-4-8": { inputPerMillion: 5.0, outputPerMillion: 25.0 },
    "claude-opus-4-7": { inputPerMillion: 5.0, outputPerMillion: 25.0 },
    "claude-opus-4-6": { inputPerMillion: 5.0, outputPerMillion: 25.0 },
    "claude-sonnet-5-5": { inputPerMillion: 2.0, outputPerMillion: 10.0 },
    // Launch pricing made permanent in September 2026 (the 3.0 / 15.0 increase was cancelled).
    "claude-sonnet-5": { inputPerMillion: 2.0, outputPerMillion: 10.0 },
    "claude-sonnet-4-6": { inputPerMillion: 3.0, outputPerMillion: 15.0 },
    // Prompts over 100K tokens bill every token at 5x.
    "claude-haiku-5-5": {
        inputPerMillion: 0.1,
        outputPerMillion: 0.5,
        longContextThreshold: 100_001,
        longContextInputPerMillion: 0.5,
        longContextOutputPerMillion: 2.5,
    },
    "claude-haiku-4-5": { inputPerMillion: 1.0, outputPerMillion: 5.0 },
    // --- Moonshot ---
    "kimi-k2.6": { inputPerMillion: 0.95, outputPerMillion: 4.0 },
    // --- xAI ---
    "grok-4.6": {
        inputPerMillion: 2.0,
        outputPerMillion: 6.0,
        longContextThreshold: 200_000,
        longContextInputPerMillion: 4.0,
        longContextOutputPerMillion: 12.0,
    },
};

export const normalizeModelForPricing = (modelName: string | null | undefined): string | null => {
    if (!modelName) return null;
    const normalized = modelName.trim().toLowerCase();
    if (!normalized) return null;

    if (MODEL_PRICING_USD_PER_MILLION[normalized]) {
        return normalized;
    }

    // Match versioned model ids like "gpt-5.5-2026-04-23" using the longest prefix first
    // so "gpt-5.5" wins over "gpt-5".
    const prefixes = Object.keys(MODEL_PRICING_USD_PER_MILLION).sort(
        (left, right) => right.length - left.length
    );
    for (const prefix of prefixes) {
        if (normalized.startsWith(prefix)) {
            return prefix;
        }
    }

    return null;
};

export const getModelPricing = (modelName: string | null | undefined): ModelPricing | null => {
    const normalized = normalizeModelForPricing(modelName);
    if (!normalized) return null;
    return MODEL_PRICING_USD_PER_MILLION[normalized] || null;
};

export const calculateCostUsd = (
    modelName: string | null | undefined,
    inputTokens: number,
    outputTokens: number
): number => {
    const pricing = getModelPricing(modelName);
    if (!pricing) return 0;

    const normalizedInputTokens = Math.max(0, inputTokens);
    const useLongContextRates =
        pricing.longContextThreshold !== undefined &&
        normalizedInputTokens >= pricing.longContextThreshold;
    const inputRate = useLongContextRates
        ? (pricing.longContextInputPerMillion ?? pricing.inputPerMillion)
        : pricing.inputPerMillion;
    const outputRate = useLongContextRates
        ? (pricing.longContextOutputPerMillion ?? pricing.outputPerMillion)
        : pricing.outputPerMillion;
    const inputCost = (normalizedInputTokens / 1_000_000) * inputRate;
    const outputCost = (Math.max(0, outputTokens) / 1_000_000) * outputRate;
    return inputCost + outputCost;
};

export const formatUsd = (
    value: number,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
    const minimumFractionDigits = options?.minimumFractionDigits ?? 2;
    const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
    return `$${value.toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits })}`;
};

export const extractMessageLlmUsage = (
    messageMetadata: Record<string, unknown> | null | undefined
): { model: string | null; inputTokens: number; outputTokens: number } => {
    if (!messageMetadata || typeof messageMetadata !== "object") {
        return { model: null, inputTokens: 0, outputTokens: 0 };
    }

    const usage = messageMetadata.llm_usage as MessageLlmUsage | undefined;
    const fallbackModel =
        typeof messageMetadata.model === "string" ? messageMetadata.model : null;

    if (!usage || typeof usage !== "object") {
        return { model: fallbackModel, inputTokens: 0, outputTokens: 0 };
    }

    const model = (typeof usage.model === "string" && usage.model.trim())
        ? usage.model.trim()
        : fallbackModel;

    const inputTokens = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
    const outputTokens = Number(usage.output_tokens ?? usage.completion_tokens ?? 0) || 0;

    return { model, inputTokens, outputTokens };
};

export const calculateMessagesCostUsd = (
    messages: Array<{ message_metadata?: Record<string, unknown> | null }> | null | undefined
): {
    totalCostUsd: number;
    inputTokens: number;
    outputTokens: number;
    pricedMessageCount: number;
    unpricedModels: string[];
} => {
    let totalCostUsd = 0;
    let inputTokens = 0;
    let outputTokens = 0;
    let pricedMessageCount = 0;
    const unpricedModels = new Set<string>();

    for (const message of messages || []) {
        const usage = extractMessageLlmUsage(message.message_metadata);
        if (usage.inputTokens === 0 && usage.outputTokens === 0) {
            continue;
        }

        inputTokens += usage.inputTokens;
        outputTokens += usage.outputTokens;
        pricedMessageCount += 1;

        const messageCost = calculateCostUsd(usage.model, usage.inputTokens, usage.outputTokens);
        totalCostUsd += messageCost;

        if (messageCost === 0 && usage.model && !getModelPricing(usage.model)) {
            unpricedModels.add(usage.model);
        }
    }

    return {
        totalCostUsd,
        inputTokens,
        outputTokens,
        pricedMessageCount,
        unpricedModels: Array.from(unpricedModels),
    };
};
